import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Button,
  InputBase,
  MenuItem,
  Select,
  Checkbox,
  IconButton,
  Tooltip,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";

import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SortRoundedIcon from "@mui/icons-material/SortRounded";
import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined";

import ApiConfig from "../../../Config";
import productService from "../../../repository/productRepository";
import collectionService from "../../../repository/collectionRepository";

const AddToCollection = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // ---------------------------------------------------------------------------
  // COLLECTION ID
  // ---------------------------------------------------------------------------

  const collectionId = location.state?.collectionId;

  // ---------------------------------------------------------------------------
  // PREMIUM THEME
  // ---------------------------------------------------------------------------

  const colors = {
    background: isDark ? "#11100F" : "#F6F3EF",
    card: isDark ? "#191715" : "#FFFFFF",
    cardAlt: isDark ? "#151412" : "#FCFBF9",
    text: isDark ? "#F4F0EA" : "#27231F",
    secondary: isDark ? "#AAA49C" : "#77716A",
    accent: isDark ? "#D1B99A" : "#806B52",
    accentSoft: isDark ? "rgba(209,185,154,.10)" : "rgba(128,107,82,.08)",
    border: isDark ? "rgba(255,255,255,.08)" : "rgba(50,40,30,.09)",
    success: isDark ? "#8FC6A3" : "#3D8054",
    successSoft: isDark ? "rgba(143,198,163,.12)" : "rgba(61,128,84,.09)",
    danger: isDark ? "#D99494" : "#B54545",
    dangerSoft: isDark ? "rgba(217,148,148,.10)" : "rgba(181,69,69,.08)",
  };

  const cardSx = {
    borderRadius: "14px",
    backgroundColor: colors.card,
    border: `1px solid ${colors.border}`,
    boxShadow: isDark
      ? "0 8px 30px rgba(0,0,0,.14)"
      : "0 8px 30px rgba(50,40,30,.045)",
  };

  // ---------------------------------------------------------------------------
  // STATE
  // ---------------------------------------------------------------------------

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [sortField, setSortField] = useState("productName");
  const [sortOrder, setSortOrder] = useState("asc");

  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(1);

  const [selectedProducts, setSelectedProducts] = useState([]);
  const [adding, setAdding] = useState(false);

  // ---------------------------------------------------------------------------
  // FETCH PRODUCTS
  // ---------------------------------------------------------------------------

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await productService.getAllProducts();

      const productsData = (response.data || []).map((item) => ({
        id: item._id,
        productId: item.product?.productId,
        productName: item.product?.title || "Untitled Product",
        product_ShortDescription: item.product?.description || "",
        s_Price: item.product?.rentAmount ?? 0,
        size: item.product?.size || "—",
        productStatus: Boolean(item.productStatus),
        productImage: item.productImage || [],
      }));

      setProducts(productsData);
    } catch (err) {
      console.error("Failed to fetch products:", err);
      setError("Failed to fetch products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  // ---------------------------------------------------------------------------
  // FILTER + SORT
  // ---------------------------------------------------------------------------

  const filteredProducts = useMemo(() => {
    let filtered = [...products];

    const search = searchTerm.trim().toLowerCase();

    if (search) {
      filtered = filtered.filter((product) => {
        return (
          product.productName.toLowerCase().includes(search) ||
          String(product.productId || "")
            .toLowerCase()
            .includes(search) ||
          String(product.id || "")
            .toLowerCase()
            .includes(search)
        );
      });
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter(
        (product) => product.productStatus === (statusFilter === "active"),
      );
    }

    filtered.sort((a, b) => {
      const first = a[sortField];
      const second = b[sortField];

      if (typeof first === "number" && typeof second === "number") {
        return sortOrder === "asc" ? first - second : second - first;
      }

      return sortOrder === "asc"
        ? String(first ?? "").localeCompare(String(second ?? ""))
        : String(second ?? "").localeCompare(String(first ?? ""));
    });

    return filtered;
  }, [products, searchTerm, statusFilter, sortField, sortOrder]);

  // ---------------------------------------------------------------------------
  // PAGINATION
  // ---------------------------------------------------------------------------

  const totalPages = Math.max(
    1,
    Math.ceil(filteredProducts.length / rowsPerPage),
  );

  const safePage = Math.min(page, totalPages);

  const displayedProducts = filteredProducts.slice(
    (safePage - 1) * rowsPerPage,
    safePage * rowsPerPage,
  );

  useEffect(() => {
    setPage(1);
  }, [searchTerm, statusFilter, rowsPerPage]);

  // ---------------------------------------------------------------------------
  // SORT
  // ---------------------------------------------------------------------------

  const handleSort = (field) => {
    const isAsc = sortField === field && sortOrder === "asc";

    setSortField(field);
    setSortOrder(isAsc ? "desc" : "asc");
  };

  // ---------------------------------------------------------------------------
  // CHECKBOX
  // ---------------------------------------------------------------------------

  const handleCheckboxChange = (productId) => {
    setSelectedProducts((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId],
    );
  };

  const allDisplayedSelected =
    displayedProducts.length > 0 &&
    displayedProducts.every((product) => selectedProducts.includes(product.id));

  const handleSelectAll = () => {
    if (allDisplayedSelected) {
      setSelectedProducts((prev) =>
        prev.filter(
          (id) => !displayedProducts.some((product) => product.id === id),
        ),
      );
    } else {
      setSelectedProducts((prev) => [
        ...new Set([
          ...prev,
          ...displayedProducts.map((product) => product.id),
        ]),
      ]);
    }
  };

  // ---------------------------------------------------------------------------
  // ADD TO COLLECTION
  // ---------------------------------------------------------------------------

  const handleAddToCollection = async () => {
    if (!collectionId) {
      setError("Collection ID is missing");
      return;
    }

    if (selectedProducts.length === 0) {
      setError("Please select at least one product");
      return;
    }

    try {
      setAdding(true);
      setError("");

      await collectionService.addProductsToCollection(
        collectionId,
        selectedProducts,
      );

      setSuccess(
        `${selectedProducts.length} product${
          selectedProducts.length > 1 ? "s" : ""
        } added to collection successfully`,
      );

      setSelectedProducts([]);

      setTimeout(() => {
        navigate(-1);
      }, 900);
    } catch (err) {
      console.error("Failed to add products:", err);

      setError(
        err?.response?.data?.message || "Failed to add products to collection",
      );
    } finally {
      setAdding(false);
    }
  };

  // ---------------------------------------------------------------------------
  // BACK
  // ---------------------------------------------------------------------------

  const handleBack = () => {
    navigate(-1);
  };

  // ---------------------------------------------------------------------------
  // LOADING
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: colors.background,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box textAlign="center">
          <CircularProgress
            size={30}
            thickness={3}
            sx={{ color: colors.accent }}
          />

          <Typography
            sx={{
              mt: 1.5,
              fontSize: 12,
              color: colors.secondary,
            }}
          >
            Loading products...
          </Typography>
        </Box>
      </Box>
    );
  }

  // ---------------------------------------------------------------------------
  // MAIN
  // ---------------------------------------------------------------------------

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: colors.background,
        color: colors.text,
        p: {
          xs: 1.5,
          sm: 2.5,
          md: 4,
        },
      }}
    >
      <Box
        sx={{
          maxWidth: 1500,
          width: "100%",
          mx: "auto",
        }}
      >
        {/* ----------------------------------------------------------------- */}
        {/* HEADER */}
        {/* ----------------------------------------------------------------- */}

        <Box
          sx={{
            display: "flex",
            alignItems: {
              xs: "flex-start",
              md: "center",
            },
            justifyContent: "space-between",
            gap: 2,
            mb: 3,
            flexDirection: {
              xs: "column",
              md: "row",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.4,
            }}
          >
            <Tooltip title="Back">
              <IconButton
                onClick={handleBack}
                sx={{
                  width: 38,
                  height: 38,
                  mt: 0.2,
                  borderRadius: "9px",
                  color: colors.secondary,
                  backgroundColor: colors.card,
                  border: `1px solid ${colors.border}`,
                  "&:hover": {
                    color: colors.accent,
                    borderColor: colors.accent,
                    backgroundColor: colors.accentSoft,
                  },
                }}
              >
                <ArrowBackRoundedIcon sx={{ fontSize: 19 }} />
              </IconButton>
            </Tooltip>

            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: "11px",
                backgroundColor: colors.accentSoft,
                color: colors.accent,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Inventory2OutlinedIcon sx={{ fontSize: 23 }} />
            </Box>

            <Box>
              <Typography
                sx={{
                  fontFamily: "Georgia, 'Times New Roman', serif",
                  fontSize: {
                    xs: 22,
                    sm: 26,
                    md: 28,
                  },
                  lineHeight: 1.15,
                  fontWeight: 600,
                  color: colors.text,
                }}
              >
                Add Products
              </Typography>

              <Typography
                sx={{
                  mt: 0.5,
                  fontSize: 11.5,
                  color: colors.secondary,
                }}
              >
                Select products to add to this collection
              </Typography>
            </Box>
          </Box>

          {/* SELECTED COUNT */}

          <Paper
            sx={{
              ...cardSx,
              px: 1.5,
              py: 1,
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: {
                xs: "100%",
                md: "auto",
              },
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: "8px",
                backgroundColor: colors.accentSoft,
                color: colors.accent,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CheckCircleOutlineRoundedIcon sx={{ fontSize: 17 }} />
            </Box>

            <Box>
              <Typography
                sx={{
                  fontSize: 9.5,
                  textTransform: "uppercase",
                  letterSpacing: ".07em",
                  color: colors.secondary,
                  fontWeight: 600,
                }}
              >
                Selected
              </Typography>

              <Typography
                sx={{
                  fontSize: 15,
                  fontWeight: 650,
                  lineHeight: 1.2,
                  color: colors.text,
                }}
              >
                {selectedProducts.length} products
              </Typography>
            </Box>
          </Paper>
        </Box>

        {/* ----------------------------------------------------------------- */}
        {/* NOTIFICATIONS */}
        {/* ----------------------------------------------------------------- */}

        {success && (
          <Box
            sx={{
              position: "fixed",
              right: {
                xs: 14,
                sm: 24,
              },
              bottom: {
                xs: 14,
                sm: 24,
              },
              zIndex: 1500,
              maxWidth: {
                xs: "calc(100vw - 28px)",
                sm: 420,
              },
            }}
          >
            <Alert
              severity="success"
              onClose={() => setSuccess("")}
              sx={{
                borderRadius: "10px",
                boxShadow: isDark
                  ? "0 12px 35px rgba(0,0,0,.35)"
                  : "0 12px 35px rgba(50,40,30,.12)",
              }}
            >
              {success}
            </Alert>
          </Box>
        )}

        {error && (
          <Box
            sx={{
              position: "fixed",
              right: {
                xs: 14,
                sm: 24,
              },
              bottom: {
                xs: 14,
                sm: 24,
              },
              zIndex: 1500,
              maxWidth: {
                xs: "calc(100vw - 28px)",
                sm: 420,
              },
            }}
          >
            <Alert
              severity="error"
              onClose={() => setError("")}
              sx={{
                borderRadius: "10px",
                boxShadow: isDark
                  ? "0 12px 35px rgba(0,0,0,.35)"
                  : "0 12px 35px rgba(50,40,30,.12)",
              }}
            >
              {error}
            </Alert>
          </Box>
        )}

        {/* ----------------------------------------------------------------- */}
        {/* TOOLBAR */}
        {/* ----------------------------------------------------------------- */}

        <Paper
          sx={{
            ...cardSx,
            p: {
              xs: 1.3,
              sm: 1.5,
            },
            mb: 2,
          }}
        >
          <Grid container spacing={1.5} alignItems="center">
            {/* Search */}

            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  height: 42,
                  display: "flex",
                  alignItems: "center",
                  borderRadius: "9px",
                  backgroundColor: colors.cardAlt,
                  border: `1px solid ${colors.border}`,
                  transition: "border-color .2s ease",
                  "&:focus-within": {
                    borderColor: colors.accent,
                  },
                }}
              >
                <SearchRoundedIcon
                  sx={{
                    ml: 1.3,
                    mr: 0.7,
                    fontSize: 19,
                    color: colors.secondary,
                  }}
                />

                <InputBase
                  fullWidth
                  placeholder="Search by product name, ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{
                    fontSize: 12,
                    color: colors.text,
                    pr: 1,
                    "& input::placeholder": {
                      color: colors.secondary,
                      opacity: 1,
                    },
                  }}
                />

                {searchTerm && (
                  <IconButton
                    size="small"
                    onClick={() => setSearchTerm("")}
                    sx={{
                      mr: 0.5,
                      color: colors.secondary,
                    }}
                  >
                    <CloseRoundedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
              </Box>
            </Grid>

            {/* Status */}

            <Grid item xs={12} sm={6} md={2.5}>
              <Select
                fullWidth
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                size="small"
                sx={{
                  height: 42,
                  borderRadius: "9px",
                  backgroundColor: colors.cardAlt,
                  color: colors.text,
                  fontSize: 12,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.border,
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.accent,
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.accent,
                  },
                }}
              >
                <MenuItem value="all">All Products</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </Grid>

            {/* Rows */}

            <Grid item xs={12} sm={6} md={2}>
              <Select
                fullWidth
                value={rowsPerPage}
                onChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
                size="small"
                sx={{
                  height: 42,
                  borderRadius: "9px",
                  backgroundColor: colors.cardAlt,
                  color: colors.text,
                  fontSize: 12,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.border,
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.accent,
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.accent,
                  },
                }}
              >
                <MenuItem value={5}>5 / page</MenuItem>
                <MenuItem value={10}>10 / page</MenuItem>
                <MenuItem value={25}>25 / page</MenuItem>
                <MenuItem value={50}>50 / page</MenuItem>
              </Select>
            </Grid>

            {/* Add */}

            <Grid item xs={12} md={2.5}>
              <Button
                fullWidth
                variant="contained"
                startIcon={
                  adding ? (
                    <CircularProgress size={15} sx={{ color: "#fff" }} />
                  ) : (
                    <AddRoundedIcon sx={{ fontSize: 18 }} />
                  )
                }
                disabled={selectedProducts.length === 0 || adding}
                onClick={handleAddToCollection}
                sx={{
                  height: 42,
                  borderRadius: "9px",
                  backgroundColor: colors.accent,
                  color: "#fff",
                  textTransform: "none",
                  fontSize: 12,
                  fontWeight: 650,
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: isDark ? "#C5AC8C" : "#705B44",
                    boxShadow: "none",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: colors.accentSoft,
                    color: colors.secondary,
                  },
                }}
              >
                {adding
                  ? "Adding..."
                  : `Add ${
                      selectedProducts.length ? selectedProducts.length : ""
                    } Selected`}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* ----------------------------------------------------------------- */}
        {/* RESULT INFO */}
        {/* ----------------------------------------------------------------- */}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1.5,
            px: 0.3,
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              color: colors.secondary,
            }}
          >
            Showing{" "}
            <Box
              component="span"
              sx={{
                color: colors.text,
                fontWeight: 600,
              }}
            >
              {filteredProducts.length === 0
                ? 0
                : (safePage - 1) * rowsPerPage + 1}
            </Box>{" "}
            –{" "}
            <Box
              component="span"
              sx={{
                color: colors.text,
                fontWeight: 600,
              }}
            >
              {Math.min(safePage * rowsPerPage, filteredProducts.length)}
            </Box>{" "}
            of{" "}
            <Box
              component="span"
              sx={{
                color: colors.text,
                fontWeight: 600,
              }}
            >
              {filteredProducts.length}
            </Box>{" "}
            products
          </Typography>

          <Typography
            sx={{
              display: {
                xs: "none",
                sm: "block",
              },
              fontSize: 10.5,
              color: colors.secondary,
            }}
          >
            {products.length} total products
          </Typography>
        </Box>

        {/* ----------------------------------------------------------------- */}
        {/* TABLE */}
        {/* ----------------------------------------------------------------- */}

        <Paper
          sx={{
            ...cardSx,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              width: "100%",
              overflowX: "auto",
            }}
          >
            <Box
              component="table"
              sx={{
                width: "100%",
                minWidth: 900,
                borderCollapse: "collapse",
              }}
            >
              {/* HEADER */}

              <Box component="thead">
                <Box component="tr">
                  <Box
                    component="th"
                    sx={{
                      width: 60,
                      textAlign: "center",
                      py: 1.4,
                      px: 1,
                      backgroundColor: colors.cardAlt,
                      borderBottom: `1px solid ${colors.border}`,
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={allDisplayedSelected}
                      indeterminate={
                        selectedProducts.length > 0 && !allDisplayedSelected
                      }
                      onChange={handleSelectAll}
                      sx={{
                        color: colors.secondary,
                        "&.Mui-checked": {
                          color: colors.accent,
                        },
                        "&.MuiCheckbox-indeterminate": {
                          color: colors.accent,
                        },
                      }}
                    />
                  </Box>

                  <Box
                    component="th"
                    onClick={() => handleSort("id")}
                    sx={{
                      textAlign: "left",
                      py: 1.4,
                      px: 1.5,
                      backgroundColor: colors.cardAlt,
                      borderBottom: `1px solid ${colors.border}`,
                      cursor: "pointer",
                      userSelect: "none",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 10,
                          textTransform: "uppercase",
                          letterSpacing: ".06em",
                          color: colors.secondary,
                          fontWeight: 650,
                        }}
                      >
                        Product ID
                      </Typography>

                      <SortRoundedIcon
                        sx={{
                          fontSize: 14,
                          color:
                            sortField === "id"
                              ? colors.accent
                              : colors.secondary,
                        }}
                      />
                    </Box>
                  </Box>

                  <Box
                    component="th"
                    onClick={() => handleSort("productName")}
                    sx={{
                      textAlign: "left",
                      py: 1.4,
                      px: 1.5,
                      backgroundColor: colors.cardAlt,
                      borderBottom: `1px solid ${colors.border}`,
                      cursor: "pointer",
                      userSelect: "none",
                      minWidth: 240,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 10,
                          textTransform: "uppercase",
                          letterSpacing: ".06em",
                          color: colors.secondary,
                          fontWeight: 650,
                        }}
                      >
                        Product
                      </Typography>

                      <SortRoundedIcon
                        sx={{
                          fontSize: 14,
                          color:
                            sortField === "productName"
                              ? colors.accent
                              : colors.secondary,
                        }}
                      />
                    </Box>
                  </Box>

                  <Box
                    component="th"
                    sx={{
                      textAlign: "left",
                      py: 1.4,
                      px: 1.5,
                      backgroundColor: colors.cardAlt,
                      borderBottom: `1px solid ${colors.border}`,
                      fontSize: 10,
                      textTransform: "uppercase",
                      letterSpacing: ".06em",
                      color: colors.secondary,
                      fontWeight: 650,
                    }}
                  >
                    Image
                  </Box>

                  <Box
                    component="th"
                    onClick={() => handleSort("s_Price")}
                    sx={{
                      textAlign: "left",
                      py: 1.4,
                      px: 1.5,
                      backgroundColor: colors.cardAlt,
                      borderBottom: `1px solid ${colors.border}`,
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 10,
                          textTransform: "uppercase",
                          letterSpacing: ".06em",
                          color: colors.secondary,
                          fontWeight: 650,
                        }}
                      >
                        Rent
                      </Typography>

                      <SortRoundedIcon
                        sx={{
                          fontSize: 14,
                          color:
                            sortField === "s_Price"
                              ? colors.accent
                              : colors.secondary,
                        }}
                      />
                    </Box>
                  </Box>

                  <Box
                    component="th"
                    sx={{
                      textAlign: "left",
                      py: 1.4,
                      px: 1.5,
                      backgroundColor: colors.cardAlt,
                      borderBottom: `1px solid ${colors.border}`,
                      fontSize: 10,
                      textTransform: "uppercase",
                      letterSpacing: ".06em",
                      color: colors.secondary,
                      fontWeight: 650,
                    }}
                  >
                    Size
                  </Box>

                  <Box
                    component="th"
                    sx={{
                      textAlign: "left",
                      py: 1.4,
                      px: 1.5,
                      backgroundColor: colors.cardAlt,
                      borderBottom: `1px solid ${colors.border}`,
                      fontSize: 10,
                      textTransform: "uppercase",
                      letterSpacing: ".06em",
                      color: colors.secondary,
                      fontWeight: 650,
                    }}
                  >
                    Status
                  </Box>
                </Box>
              </Box>

              {/* BODY */}

              <Box component="tbody">
                {displayedProducts.map((product) => {
                  const selected = selectedProducts.includes(product.id);

                  return (
                    <Box
                      component="tr"
                      key={product.id}
                      sx={{
                        backgroundColor: selected
                          ? colors.accentSoft
                          : "transparent",
                        transition: "background-color .15s ease",
                        "&:hover": {
                          backgroundColor: selected
                            ? colors.accentSoft
                            : colors.cardAlt,
                        },
                      }}
                    >
                      {/* CHECKBOX */}

                      <Box
                        component="td"
                        sx={{
                          textAlign: "center",
                          py: 1.2,
                          px: 1,
                          borderBottom: `1px solid ${colors.border}`,
                        }}
                      >
                        <Checkbox
                          size="small"
                          checked={selected}
                          onChange={() => handleCheckboxChange(product.id)}
                          sx={{
                            color: colors.secondary,
                            "&.Mui-checked": {
                              color: colors.accent,
                            },
                          }}
                        />
                      </Box>

                      {/* ID */}

                      <Box
                        component="td"
                        sx={{
                          py: 1.2,
                          px: 1.5,
                          borderBottom: `1px solid ${colors.border}`,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 10,
                            color: colors.secondary,
                            fontFamily: "monospace",
                            maxWidth: 140,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={product.id}
                        >
                          {product.id}
                        </Typography>
                      </Box>

                      {/* PRODUCT */}

                      <Box
                        component="td"
                        sx={{
                          py: 1.2,
                          px: 1.5,
                          borderBottom: `1px solid ${colors.border}`,
                        }}
                      >
                        <Box>
                          <Typography
                            sx={{
                              fontSize: 12,
                              fontWeight: 650,
                              color: colors.text,
                              lineHeight: 1.35,
                            }}
                          >
                            {product.productName}
                          </Typography>

                          {product.productId && (
                            <Typography
                              sx={{
                                mt: 0.25,
                                fontSize: 9.5,
                                color: colors.secondary,
                              }}
                            >
                              SKU: {product.productId}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      {/* IMAGE */}

                      <Box
                        component="td"
                        sx={{
                          py: 1,
                          px: 1.5,
                          borderBottom: `1px solid ${colors.border}`,
                        }}
                      >
                        {product.productImage?.[0] ? (
                          <Box
                            component="img"
                            src={product.productImage[0]}
                            alt={product.productName}
                            sx={{
                              width: 58,
                              height: 58,
                              objectFit: "contain",
                              display: "block",
                              borderRadius: "9px",
                              backgroundColor: colors.cardAlt,
                              border: `1px solid ${colors.border}`,
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: 58,
                              height: 58,
                              borderRadius: "9px",
                              backgroundColor: colors.cardAlt,
                              border: `1px solid ${colors.border}`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: colors.secondary,
                            }}
                          >
                            <ImageNotSupportedOutlinedIcon
                              sx={{ fontSize: 20 }}
                            />
                          </Box>
                        )}
                      </Box>

                      {/* RENT */}

                      <Box
                        component="td"
                        sx={{
                          py: 1.2,
                          px: 1.5,
                          borderBottom: `1px solid ${colors.border}`,
                        }}
                      >
                        <Chip
                          label={`₹${product.s_Price}`}
                          size="small"
                          sx={{
                            height: 25,
                            borderRadius: "7px",
                            fontSize: 10,
                            fontWeight: 650,
                            backgroundColor: colors.successSoft,
                            color: colors.success,
                          }}
                        />
                      </Box>

                      {/* SIZE */}

                      <Box
                        component="td"
                        sx={{
                          py: 1.2,
                          px: 1.5,
                          borderBottom: `1px solid ${colors.border}`,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 11.5,
                            color: colors.text,
                            fontWeight: 500,
                          }}
                        >
                          {product.size}
                        </Typography>
                      </Box>

                      {/* STATUS */}

                      <Box
                        component="td"
                        sx={{
                          py: 1.2,
                          px: 1.5,
                          borderBottom: `1px solid ${colors.border}`,
                        }}
                      >
                        <Chip
                          label={product.productStatus ? "Active" : "Inactive"}
                          size="small"
                          sx={{
                            height: 25,
                            borderRadius: "7px",
                            fontSize: 10,
                            fontWeight: 600,
                            backgroundColor: product.productStatus
                              ? colors.successSoft
                              : colors.dangerSoft,
                            color: product.productStatus
                              ? colors.success
                              : colors.danger,
                          }}
                        />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>

          {/* ---------------------------------------------------------------- */}
          {/* EMPTY SEARCH STATE */}
          {/* ---------------------------------------------------------------- */}

          {displayedProducts.length === 0 && (
            <Box
              sx={{
                py: 8,
                px: 3,
                textAlign: "center",
              }}
            >
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  mx: "auto",
                  borderRadius: "13px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: colors.accentSoft,
                  color: colors.accent,
                }}
              >
                <SearchRoundedIcon sx={{ fontSize: 26 }} />
              </Box>

              <Typography
                sx={{
                  mt: 1.5,
                  fontFamily: "Georgia, 'Times New Roman', serif",
                  fontSize: 21,
                  fontWeight: 600,
                  color: colors.text,
                }}
              >
                No products found
              </Typography>

              <Typography
                sx={{
                  mt: 0.6,
                  fontSize: 11.5,
                  color: colors.secondary,
                }}
              >
                Try changing your search or filters.
              </Typography>
            </Box>
          )}

          {/* ---------------------------------------------------------------- */}
          {/* PAGINATION */}
          {/* ---------------------------------------------------------------- */}

          {filteredProducts.length > 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1,
                p: 1.5,
                borderTop: `1px solid ${colors.border}`,
                flexDirection: {
                  xs: "column",
                  sm: "row",
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: 10.5,
                  color: colors.secondary,
                }}
              >
                Page {safePage} of {totalPages}
              </Typography>

              <Stack direction="row" spacing={0.7}>
                <Button
                  size="small"
                  disabled={safePage <= 1}
                  onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                  sx={{
                    minWidth: 70,
                    height: 32,
                    borderRadius: "7px",
                    textTransform: "none",
                    fontSize: 11,
                    color: colors.secondary,
                    border: `1px solid ${colors.border}`,
                  }}
                >
                  Previous
                </Button>

                <Button
                  size="small"
                  disabled={safePage >= totalPages}
                  onClick={() =>
                    setPage((prev) => Math.min(totalPages, prev + 1))
                  }
                  sx={{
                    minWidth: 70,
                    height: 32,
                    borderRadius: "7px",
                    textTransform: "none",
                    fontSize: 11,
                    color: colors.text,
                    border: `1px solid ${colors.border}`,
                    "&:hover": {
                      borderColor: colors.accent,
                      color: colors.accent,
                    },
                  }}
                >
                  Next
                </Button>
              </Stack>
            </Box>
          )}
        </Paper>

        {/* ----------------------------------------------------------------- */}
        {/* MOBILE SELECTED BAR */}
        {/* ----------------------------------------------------------------- */}

        {isMobile && selectedProducts.length > 0 && (
          <Paper
            sx={{
              position: "fixed",
              left: 12,
              right: 12,
              bottom: 12,
              zIndex: 1200,
              ...cardSx,
              p: 1.2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: 10,
                  color: colors.secondary,
                }}
              >
                Selected
              </Typography>

              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 650,
                  color: colors.text,
                }}
              >
                {selectedProducts.length} products
              </Typography>
            </Box>

            <Button
              variant="contained"
              onClick={handleAddToCollection}
              disabled={adding}
              startIcon={
                adding ? (
                  <CircularProgress size={14} sx={{ color: "#fff" }} />
                ) : (
                  <AddRoundedIcon sx={{ fontSize: 17 }} />
                )
              }
              sx={{
                height: 38,
                borderRadius: "8px",
                backgroundColor: colors.accent,
                color: "#fff",
                textTransform: "none",
                fontSize: 11,
                fontWeight: 650,
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: isDark ? "#C5AC8C" : "#705B44",
                  boxShadow: "none",
                },
              }}
            >
              Add
            </Button>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default AddToCollection;
